import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSession } from '@/lib/session';
import {
  fetchInventory,
  upsertInventoryItem,
  deleteInventoryItem,
  fetchWastage,
  addWastage,
  fetchMenu,
  fetchRecipe,
  fetchRecipedMenuIds,
  saveRecipe,
  fetchPurchases,
  createPurchase,
  fetchConsumption,
  type InventoryItem,
  type WastageEntry,
  type MenuItem,
  type Purchase,
  type ConsumptionRow,
} from '@/lib/data';

type CategoryFilter = 'All' | 'Dairy' | 'Meat' | 'Vegetables' | 'Grains' | 'Oils' | 'Spices' | 'Dry Fruits';
type StockFilter = 'All' | 'Low Stock' | 'OK';
type Tab = 'stock' | 'recipes' | 'purchases' | 'wastage' | 'consumption';

const categoryFilters: CategoryFilter[] = ['All', 'Dairy', 'Meat', 'Vegetables', 'Grains', 'Oils', 'Spices', 'Dry Fruits'];
const stockFilters: StockFilter[] = ['All', 'Low Stock', 'OK'];

const itemValue = (i: InventoryItem) => i.currentStock * i.unitCost;

type InvFormData = {
  name: string;
  category: string;
  unit: string;
  currentStock: string;
  reorderPoint: string;
  optimalStock: string;
  unitCost: string;
  supplier: string;
  shelfLife: string;
};

const emptyInvForm: InvFormData = {
  name: '', category: '', unit: 'kg', currentStock: '', reorderPoint: '',
  optimalStock: '', unitCost: '', supplier: '', shelfLife: '',
};

type WastageFormData = {
  itemName: string;
  quantity: string;
  unit: string;
  reason: string;
};

const emptyWastageForm: WastageFormData = {
  itemName: '', quantity: '', unit: 'kg', reason: 'Spoilage',
};

type RecipeLine = { inventoryItemId: string; qtyPerUnit: string };
type PurchaseLine = { inventoryItemId: string; qty: string; unitCost: string };

function todayISO() { return new Date().toISOString().slice(0, 10); }
function monthStartISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export default function InventoryPage() {
  const { user, restaurant } = useSession();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('All');
  const [stockFilter, setStockFilter] = useState<StockFilter>('All');
  const [sortKey, setSortKey] = useState<'name' | 'currentStock' | 'totalValue'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [activeTab, setActiveTab] = useState<Tab>('stock');

  const [invItems, setInvItems] = useState<InventoryItem[]>([]);
  const [wastageEntries, setWastageEntries] = useState<WastageEntry[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [recipedIds, setRecipedIds] = useState<Set<string>>(new Set());
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [saving, setSaving] = useState(false);

  // Add / Edit Inventory
  const [invModalOpen, setInvModalOpen] = useState(false);
  const [editingInvItem, setEditingInvItem] = useState<InventoryItem | null>(null);
  const [invForm, setInvForm] = useState<InvFormData>({ ...emptyInvForm });

  // Record Wastage modal
  const [wastageModalOpen, setWastageModalOpen] = useState(false);
  const [wastageForm, setWastageForm] = useState<WastageFormData>({ ...emptyWastageForm });

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<InventoryItem | null>(null);

  // Recipe editor
  const [recipeMenu, setRecipeMenu] = useState<MenuItem | null>(null);
  const [recipeLines, setRecipeLines] = useState<RecipeLine[]>([]);
  const [recipeSearch, setRecipeSearch] = useState('');

  // Purchase order
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [poSupplier, setPoSupplier] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [poLines, setPoLines] = useState<PurchaseLine[]>([{ inventoryItemId: '', qty: '', unitCost: '' }]);

  // Consumption report
  const [consFrom, setConsFrom] = useState(monthStartISO());
  const [consTo, setConsTo] = useState(todayISO());
  const [consRows, setConsRows] = useState<ConsumptionRow[]>([]);
  const [consLoading, setConsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [items, wastage, menu, recipedList, pos] = await Promise.all([
        fetchInventory(restaurant.id),
        fetchWastage(restaurant.id),
        fetchMenu(restaurant.id),
        fetchRecipedMenuIds(restaurant.id),
        fetchPurchases(restaurant.id),
      ]);
      setInvItems(items);
      setWastageEntries(wastage);
      setMenuItems(menu);
      setRecipedIds(new Set(recipedList));
      setPurchases(pos);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load inventory.');
    } finally {
      setLoading(false);
    }
  }, [restaurant.id]);

  useEffect(() => { load(); }, [load]);

  const loadConsumption = useCallback(async () => {
    setConsLoading(true);
    try {
      const from = new Date(consFrom + 'T00:00:00').toISOString();
      const to = new Date(consTo + 'T23:59:59').toISOString();
      setConsRows(await fetchConsumption(from, to));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not load report.');
      setTimeout(() => setActionError(''), 4000);
    } finally {
      setConsLoading(false);
    }
  }, [consFrom, consTo]);

  useEffect(() => {
    if (activeTab === 'consumption') loadConsumption();
  }, [activeTab, loadConsumption]);

  const showActionError = (e: unknown) => {
    setActionError(e instanceof Error ? e.message : 'Something went wrong.');
    setTimeout(() => setActionError(''), 4000);
  };

  const filteredItems = useMemo(() => {
    let result = [...invItems];

    if (categoryFilter !== 'All') {
      result = result.filter((item) => item.category === categoryFilter);
    }
    if (stockFilter === 'Low Stock') {
      result = result.filter((item) => item.currentStock <= item.reorderPoint);
    } else if (stockFilter === 'OK') {
      result = result.filter((item) => item.currentStock > item.reorderPoint);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          (item.supplier ?? '').toLowerCase().includes(q) ||
          (item.category ?? '').toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortKey === 'currentStock') cmp = a.currentStock - b.currentStock;
      else cmp = itemValue(a) - itemValue(b);
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [invItems, search, categoryFilter, stockFilter, sortKey, sortDir]);

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const lowStockItems = invItems.filter((i) => i.currentStock <= i.reorderPoint);

  const invById = useMemo(() => {
    const m = new Map<string, InventoryItem>();
    invItems.forEach((i) => m.set(i.id, i));
    return m;
  }, [invItems]);

  // --- Inventory Add/Edit ---
  const openAddInvModal = () => {
    setEditingInvItem(null);
    setInvForm({ ...emptyInvForm });
    setInvModalOpen(true);
  };

  const openEditInvModal = (item: InventoryItem) => {
    setEditingInvItem(item);
    setInvForm({
      name: item.name,
      category: item.category ?? '',
      unit: item.unit,
      currentStock: String(item.currentStock),
      reorderPoint: String(item.reorderPoint),
      optimalStock: String(item.optimalStock),
      unitCost: String(item.unitCost),
      supplier: item.supplier ?? '',
      shelfLife: item.shelfLife ?? '',
    });
    setInvModalOpen(true);
  };

  const handleInvSave = async () => {
    if (!invForm.name.trim() || !invForm.category || !invForm.unitCost || saving) return;
    setSaving(true);
    try {
      const base = {
        name: invForm.name.trim(),
        category: invForm.category,
        unit: invForm.unit,
        currentStock: Number(invForm.currentStock) || 0,
        reorderPoint: Number(invForm.reorderPoint) || 0,
        optimalStock: Number(invForm.optimalStock) || 0,
        unitCost: Number(invForm.unitCost),
        supplier: invForm.supplier.trim() || null,
        shelfLife: invForm.shelfLife.trim() || null,
      };
      if (editingInvItem) {
        const saved = await upsertInventoryItem(restaurant.id, { ...base, id: editingInvItem.id });
        setInvItems((prev) => prev.map((it) => (it.id === saved.id ? saved : it)));
      } else {
        const saved = await upsertInventoryItem(restaurant.id, {
          ...base,
          lastRestocked: new Date().toISOString(),
          wastageYtd: 0,
        });
        setInvItems((prev) => [...prev, saved]);
      }
      setInvModalOpen(false);
      setEditingInvItem(null);
    } catch (e) {
      showActionError(e);
    } finally {
      setSaving(false);
    }
  };

  const handleInvDelete = async (item: InventoryItem) => {
    try {
      await deleteInventoryItem(restaurant.id, item.id);
      setInvItems((prev) => prev.filter((i) => i.id !== item.id));
      setDeleteTarget(null);
    } catch (e) {
      showActionError(e);
    }
  };

  const handleRestock = async (item: InventoryItem) => {
    try {
      const saved = await upsertInventoryItem(restaurant.id, {
        id: item.id,
        name: item.name,
        currentStock: Math.max(item.optimalStock, item.currentStock),
        lastRestocked: new Date().toISOString(),
      });
      setInvItems((prev) => prev.map((it) => (it.id === saved.id ? saved : it)));
    } catch (e) {
      showActionError(e);
    }
  };

  // --- Wastage ---
  const openWastageModal = () => {
    setWastageForm({ ...emptyWastageForm });
    setWastageModalOpen(true);
  };

  const handleWastageSave = async () => {
    if (!wastageForm.itemName.trim() || !wastageForm.quantity || saving) return;
    setSaving(true);
    try {
      const matchingItem = invItems.find((i) => i.name.toLowerCase() === wastageForm.itemName.trim().toLowerCase());
      const unitCost = matchingItem ? matchingItem.unitCost : 0;
      const qty = Number(wastageForm.quantity);
      const value = qty * unitCost;

      const entry = await addWastage(restaurant.id, {
        itemName: wastageForm.itemName.trim(),
        quantity: qty,
        unit: wastageForm.unit,
        reason: wastageForm.reason,
        value,
        recordedBy: user.displayName || user.email || user.phoneNumber || null,
      });
      setWastageEntries((prev) => [entry, ...prev]);
      // The DB trigger deducts stock + updates wastageYtd; refetch to reflect it.
      setInvItems(await fetchInventory(restaurant.id));
      setWastageModalOpen(false);
    } catch (e) {
      showActionError(e);
    } finally {
      setSaving(false);
    }
  };

  // --- Recipe editor ---
  const openRecipeEditor = async (mi: MenuItem) => {
    setRecipeMenu(mi);
    setRecipeLines([{ inventoryItemId: '', qtyPerUnit: '' }]);
    try {
      const existing = await fetchRecipe(restaurant.id, mi.id);
      if (existing.length) {
        setRecipeLines(existing.map((r) => ({
          inventoryItemId: r.inventoryItemId,
          qtyPerUnit: String(r.qtyPerUnit),
        })));
      }
    } catch (e) {
      showActionError(e);
    }
  };

  const handleRecipeSave = async () => {
    if (!recipeMenu || saving) return;
    setSaving(true);
    try {
      const lines = recipeLines
        .filter((l) => l.inventoryItemId && Number(l.qtyPerUnit) > 0)
        .map((l) => ({
          inventoryItemId: l.inventoryItemId,
          qtyPerUnit: Number(l.qtyPerUnit),
          unit: invById.get(l.inventoryItemId)?.unit ?? null,
        }));
      await saveRecipe(restaurant.id, recipeMenu.id, recipeMenu.name, lines);
      setRecipedIds((prev) => {
        const n = new Set(prev);
        if (lines.length) n.add(recipeMenu.id); else n.delete(recipeMenu.id);
        return n;
      });
      setRecipeMenu(null);
    } catch (e) {
      showActionError(e);
    } finally {
      setSaving(false);
    }
  };

  const recipeCost = useMemo(() => {
    return recipeLines.reduce((s, l) => {
      const inv = invById.get(l.inventoryItemId);
      return s + (inv ? inv.unitCost * (Number(l.qtyPerUnit) || 0) : 0);
    }, 0);
  }, [recipeLines, invById]);

  // --- Purchase order ---
  const openPurchaseModal = () => {
    setPoSupplier('');
    setPoNumber('');
    setPoLines([{ inventoryItemId: '', qty: '', unitCost: '' }]);
    setPurchaseModalOpen(true);
  };

  const poTotal = useMemo(
    () => poLines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unitCost) || 0), 0),
    [poLines],
  );

  const handlePurchaseSave = async () => {
    if (saving) return;
    const lines = poLines
      .filter((l) => l.inventoryItemId && Number(l.qty) > 0)
      .map((l) => ({
        inventoryItemId: l.inventoryItemId,
        itemName: invById.get(l.inventoryItemId)?.name ?? '',
        qty: Number(l.qty),
        unitCost: Number(l.unitCost) || 0,
      }));
    if (!lines.length) { showActionError(new Error('Add at least one item.')); return; }
    setSaving(true);
    try {
      await createPurchase(
        restaurant.id,
        { supplier: poSupplier.trim() || undefined, poNumber: poNumber.trim() || undefined, status: 'received' },
        lines,
      );
      // Trigger added stock; refetch items + purchases.
      const [items, pos] = await Promise.all([
        fetchInventory(restaurant.id),
        fetchPurchases(restaurant.id),
      ]);
      setInvItems(items);
      setPurchases(pos);
      setPurchaseModalOpen(false);
    } catch (e) {
      showActionError(e);
    } finally {
      setSaving(false);
    }
  };

  const totalStockValue = invItems.reduce((s, i) => s + itemValue(i), 0);
  const monthName = new Date().toLocaleDateString('en-IN', { month: 'long' });
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const wastageThisMonth = wastageEntries
    .filter((e) => new Date(e.recordedAt) >= monthStart)
    .reduce((s, e) => s + e.value, 0);
  const categoryCount = new Set(invItems.map((i) => i.category).filter(Boolean)).size;

  const filteredMenu = useMemo(() => {
    const q = recipeSearch.trim().toLowerCase();
    return menuItems.filter((m) => !q || m.name.toLowerCase().includes(q));
  }, [menuItems, recipeSearch]);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'stock', label: 'Stock List' },
    { key: 'recipes', label: 'Recipes' },
    { key: 'purchases', label: 'Purchases' },
    { key: 'wastage', label: 'Wastage Log' },
    { key: 'consumption', label: 'Consumption' },
  ];

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground-800">Inventory</h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            {invItems.length} items tracked · {lowStockItems.length} need reorder · {recipedIds.size} recipes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openAddInvModal}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1"></i> Add Item
          </button>
          <button className="px-4 py-2 text-sm font-medium rounded-lg border border-background-200 bg-background-50 text-foreground-700 hover:border-background-300 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-download-2-line mr-1"></i> Export
          </button>
        </div>
      </div>

      {/* Action Error Toast */}
      {actionError && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2 shadow-sm animate-[slideInRight_0.25s_ease-out]">
          <i className="ri-error-warning-line text-red-500"></i>
          {actionError}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Total Stock Value</p>
          <p className="text-xl font-bold text-foreground-800">₹{totalStockValue.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Low Stock Items</p>
          <p className="text-xl font-bold text-accent-500">{lowStockItems.length}</p>
          <p className="text-xs text-accent-500/80">requires reorder</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Wastage ({monthName})</p>
          <p className="text-xl font-bold text-red-500">₹{wastageThisMonth.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Menu Recipes</p>
          <p className="text-xl font-bold text-foreground-800">{recipedIds.size}<span className="text-sm text-foreground-400">/{menuItems.length}</span></p>
        </div>
      </div>

      {/* Low-stock alert banner */}
      {lowStockItems.length > 0 && activeTab !== 'consumption' && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <i className="ri-alarm-warning-line text-amber-500"></i>
          <span className="font-medium">{lowStockItems.length}</span> item{lowStockItems.length > 1 ? 's' : ''} at or below reorder level:
          <span className="text-amber-700">{lowStockItems.slice(0, 4).map((i) => i.name).join(', ')}{lowStockItems.length > 4 ? '…' : ''}</span>
        </div>
      )}

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === t.key
                ? 'bg-primary-500 text-background-50'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loadError && (
        <div className="bg-background-50 border border-background-200 rounded-lg py-16 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-xl text-red-500"></i>
          </div>
          <p className="text-sm text-foreground-500 mb-3">{loadError}</p>
          <button
            onClick={load}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            Retry
          </button>
        </div>
      )}

      {loading && !loadError && (
        <div className="bg-background-50 border border-background-200 rounded-lg py-16 text-center">
          <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
          <p className="text-sm text-foreground-500 mt-2">Loading inventory...</p>
        </div>
      )}

      {!loading && !loadError && activeTab === 'stock' && (
        <>
          {/* Search + Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
              <input
                type="text"
                placeholder="Search items, suppliers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-full"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {categoryFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setCategoryFilter(f)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                    categoryFilter === f
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
                  }`}
                >
                  {f}
                </button>
              ))}
              <span className="w-px h-5 bg-background-300 mx-1"></span>
              {stockFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setStockFilter(f)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                    stockFilter === f
                      ? 'bg-accent-500 text-background-50 border-accent-500'
                      : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-100 border-b border-background-200">
                  <tr>
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer whitespace-nowrap hover:text-foreground-700"
                      onClick={() => toggleSort('name')}
                    >
                      Item {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Category</th>
                    <th
                      className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer whitespace-nowrap hover:text-foreground-700"
                      onClick={() => toggleSort('currentStock')}
                    >
                      Stock {sortKey === 'currentStock' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Unit Cost</th>
                    <th
                      className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer whitespace-nowrap hover:text-foreground-700"
                      onClick={() => toggleSort('totalValue')}
                    >
                      Value {sortKey === 'totalValue' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Supplier</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Last Restocked</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {filteredItems.map((item) => {
                    const isLow = item.currentStock <= item.reorderPoint;
                    const stockPct = item.optimalStock > 0
                      ? Math.min(100, Math.round((item.currentStock / item.optimalStock) * 100))
                      : 0;
                    return (
                      <tr key={item.id} className={`hover:bg-background-100/50 transition-colors ${isLow ? 'bg-red-50/50' : ''}`}>
                        <td className="px-4 py-3">
                          <span className="font-medium text-foreground-800">{item.name}</span>
                          <p className="text-xs text-foreground-400">{item.shelfLife ?? '—'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                            {item.category ?? '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div>
                            <span className={`font-medium ${isLow ? 'text-red-600' : 'text-foreground-800'}`}>
                              {item.currentStock} {item.unit}
                            </span>
                            <div className="w-full max-w-20 h-1.5 bg-background-200 rounded-full mt-1 ml-auto">
                              <div
                                className={`h-full rounded-full transition-all ${isLow ? 'bg-red-500' : stockPct > 60 ? 'bg-green-500' : 'bg-amber-500'}`}
                                style={{ width: `${stockPct}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-foreground-600 whitespace-nowrap">₹{item.unitCost}/{item.unit}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-medium text-foreground-800 whitespace-nowrap">₹{itemValue(item).toLocaleString('en-IN')}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{item.supplier ?? '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{item.lastRestocked ? formatShortDate(item.lastRestocked) : '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          {isLow ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium bg-red-100 text-red-700 whitespace-nowrap">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> Reorder
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium bg-green-100 text-green-700 whitespace-nowrap">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> OK
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleRestock(item)}
                              className="w-7 h-7 rounded-md hover:bg-background-200 flex items-center justify-center transition-colors cursor-pointer"
                              title="Restock to optimal"
                            >
                              <i className="ri-inbox-unarchive-line text-xs text-green-600"></i>
                            </button>
                            <button
                              onClick={() => openEditInvModal(item)}
                              className="w-7 h-7 rounded-md hover:bg-background-200 flex items-center justify-center transition-colors cursor-pointer"
                              title="Edit"
                            >
                              <i className="ri-edit-line text-xs text-foreground-500"></i>
                            </button>
                            <button
                              onClick={() => setDeleteTarget(item)}
                              className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center transition-colors cursor-pointer"
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line text-xs text-red-400"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredItems.length === 0 && (
              <div className="py-16 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                  <i className={`${invItems.length === 0 ? 'ri-archive-line' : 'ri-search-line'} text-xl text-foreground-300`}></i>
                </div>
                <p className="text-sm text-foreground-500">
                  {invItems.length === 0 ? 'No inventory items yet — add your first item to start tracking stock' : 'No items match your filters'}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* --- Recipes tab --- */}
      {!loading && !loadError && activeTab === 'recipes' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
              <input
                type="text"
                placeholder="Search menu items..."
                value={recipeSearch}
                onChange={(e) => setRecipeSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-full"
              />
            </div>
            <p className="text-xs text-foreground-500">Map each dish to its ingredients — stock auto-deducts when the order is paid.</p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-background-100 border-b border-background-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Menu Item</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Category</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Price</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Recipe</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {filteredMenu.map((mi) => {
                  const hasRecipe = recipedIds.has(mi.id);
                  return (
                    <tr key={mi.id} className="hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-800">{mi.name}</td>
                      <td className="px-4 py-3 text-foreground-500">{mi.category ?? '—'}</td>
                      <td className="px-4 py-3 text-right text-foreground-700">₹{mi.price}</td>
                      <td className="px-4 py-3 text-center">
                        {hasRecipe ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium bg-green-100 text-green-700 whitespace-nowrap">
                            <i className="ri-check-line"></i> Set
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium bg-background-100 text-foreground-400 whitespace-nowrap">Not set</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => openRecipeEditor(mi)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-book-2-line mr-1"></i>{hasRecipe ? 'Edit' : 'Add'} Recipe
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredMenu.length === 0 && (
              <div className="py-16 text-center text-sm text-foreground-500">No menu items found.</div>
            )}
          </div>
        </div>
      )}

      {/* --- Purchases tab --- */}
      {!loading && !loadError && activeTab === 'purchases' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-foreground-500">Record goods received — stock and unit cost update automatically.</p>
            <button
              onClick={openPurchaseModal}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i> New Purchase
            </button>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-background-100 border-b border-background-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">PO / Ref</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Supplier</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Total</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {purchases.map((p) => (
                  <tr key={p.id} className="hover:bg-background-100/50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-foreground-600">{p.poNumber || p.id.slice(0, 8)}</td>
                    <td className="px-4 py-3 text-foreground-700">{p.supplier ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-medium text-foreground-800">₹{p.total.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                        p.status === 'received' ? 'bg-green-100 text-green-700'
                        : p.status === 'cancelled' ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                      }`}>{p.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{p.receivedAt ? formatDateTime(p.receivedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {purchases.length === 0 && (
              <div className="py-16 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-shopping-cart-2-line text-xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-500">No purchases recorded yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Wastage tab --- */}
      {!loading && !loadError && activeTab === 'wastage' && (
        <div className="space-y-4">
          {/* Wastage summary */}
          <div className="flex items-center justify-between">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
              <div className="bg-background-50 border border-background-200 rounded-lg p-4">
                <p className="text-xs text-foreground-500">Total Wastage ({monthName})</p>
                <p className="text-xl font-bold text-red-500">₹{wastageThisMonth.toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-4">
                <p className="text-xs text-foreground-500">Entries</p>
                <p className="text-xl font-bold text-foreground-800">{wastageEntries.length}</p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-4">
                <p className="text-xs text-foreground-500">YTD Wastage Value</p>
                <p className="text-xl font-bold text-foreground-800">₹{invItems.reduce((sum, i) => sum + i.wastageYtd * i.unitCost, 0).toFixed(0)}</p>
              </div>
            </div>
            <button
              onClick={openWastageModal}
              className="ml-4 px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-background-50 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap shrink-0"
            >
              <i className="ri-add-line mr-1"></i> Record Wastage
            </button>
          </div>

          {/* Wastage Table */}
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-100 border-b border-background-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">ID</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Item</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Quantity</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Reason</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Value Lost</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Recorded By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {wastageEntries.map((entry: WastageEntry) => (
                    <tr key={entry.id} className="hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground-400 font-mono">{entry.id.slice(0, 8)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-foreground-800">{entry.itemName}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-foreground-700">{entry.quantity} {entry.unit ?? ''}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">{entry.reason ?? '—'}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-red-600 whitespace-nowrap">₹{entry.value.toLocaleString('en-IN')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-600">{entry.recordedBy ?? '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDateTime(entry.recordedAt)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {wastageEntries.length === 0 && (
              <div className="py-16 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-delete-bin-line text-xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-500">No wastage recorded yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Consumption / variance tab --- */}
      {!loading && !loadError && activeTab === 'consumption' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1">From</label>
              <input type="date" value={consFrom} max={consTo} onChange={(e) => setConsFrom(e.target.value)}
                className="px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1">To</label>
              <input type="date" value={consTo} min={consFrom} max={todayISO()} onChange={(e) => setConsTo(e.target.value)}
                className="px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
            </div>
            <button onClick={loadConsumption} className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">
              <i className="ri-refresh-line mr-1"></i> Run
            </button>
            <p className="text-xs text-foreground-500 ml-auto self-center">Consumed = recipe deductions on paid orders (theoretical usage).</p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-100 border-b border-background-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Item</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Purchased</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Consumed</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Wasted</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">In Stock</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Stock Value</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Reorder</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {consRows.map((r) => {
                    const low = r.currentStock <= r.reorderPoint;
                    return (
                      <tr key={r.inventoryItemId} className={`hover:bg-background-100/50 transition-colors ${low ? 'bg-red-50/40' : ''}`}>
                        <td className="px-4 py-3 font-medium text-foreground-800">{r.name}</td>
                        <td className="px-4 py-3 text-right text-green-700">{r.purchased ? `+${r.purchased}` : '—'} <span className="text-foreground-400 text-xs">{r.unit}</span></td>
                        <td className="px-4 py-3 text-right text-foreground-700">{r.consumed || '—'} <span className="text-foreground-400 text-xs">{r.unit}</span></td>
                        <td className="px-4 py-3 text-right text-red-600">{r.wasted || '—'} <span className="text-foreground-400 text-xs">{r.unit}</span></td>
                        <td className={`px-4 py-3 text-right font-medium ${low ? 'text-red-600' : 'text-foreground-800'}`}>{r.currentStock} <span className="text-foreground-400 text-xs">{r.unit}</span></td>
                        <td className="px-4 py-3 text-right text-foreground-700">₹{Math.round(r.stockValue).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-center">
                          {low
                            ? <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">Reorder</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">OK</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {consLoading && <div className="py-10 text-center"><i className="ri-loader-4-line text-xl text-primary-500 animate-spin"></i></div>}
            {!consLoading && consRows.length === 0 && (
              <div className="py-16 text-center text-sm text-foreground-500">No movements in this period.</div>
            )}
          </div>
        </div>
      )}

      {/* --- Add/Edit Inventory Modal --- */}
      {invModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh]">
          <div className="absolute inset-0 bg-black/30" onClick={() => { setInvModalOpen(false); setEditingInvItem(null); }}></div>
          <div className="relative w-full max-w-lg bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out] max-h-[88vh] overflow-y-auto">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-semibold text-foreground-800">{editingInvItem ? 'Edit Inventory Item' : 'Add Inventory Item'}</h3>
              <button onClick={() => { setInvModalOpen(false); setEditingInvItem(null); }} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Item Name *</label>
                  <input type="text" value={invForm.name} onChange={(e) => setInvForm({ ...invForm, name: e.target.value })} placeholder="e.g. Fresh Paneer" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Category *</label>
                  <select value={invForm.category} onChange={(e) => setInvForm({ ...invForm, category: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                    <option value="">Select</option>
                    {['Dairy','Meat','Vegetables','Grains','Oils','Spices','Dry Fruits'].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Unit</label>
                  <select value={invForm.unit} onChange={(e) => setInvForm({ ...invForm, unit: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                    {['kg','g','L','ml','pcs','pack'].map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Current Stock</label>
                  <input type="number" value={invForm.currentStock} onChange={(e) => setInvForm({ ...invForm, currentStock: e.target.value })} placeholder="0" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Reorder Point</label>
                  <input type="number" value={invForm.reorderPoint} onChange={(e) => setInvForm({ ...invForm, reorderPoint: e.target.value })} placeholder="0" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Optimal Stock</label>
                  <input type="number" value={invForm.optimalStock} onChange={(e) => setInvForm({ ...invForm, optimalStock: e.target.value })} placeholder="0" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Unit Cost (₹) *</label>
                  <input type="number" value={invForm.unitCost} onChange={(e) => setInvForm({ ...invForm, unitCost: e.target.value })} placeholder="0" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Supplier</label>
                  <input type="text" value={invForm.supplier} onChange={(e) => setInvForm({ ...invForm, supplier: e.target.value })} placeholder="Supplier name" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Shelf Life</label>
                  <input type="text" value={invForm.shelfLife} onChange={(e) => setInvForm({ ...invForm, shelfLife: e.target.value })} placeholder="e.g. 3 days" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-3 border-t border-background-200">
                <button onClick={handleInvSave} disabled={saving} className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {saving ? 'Saving...' : editingInvItem ? 'Save Changes' : 'Add Item'}
                </button>
                <button onClick={() => { setInvModalOpen(false); setEditingInvItem(null); }} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Recipe Editor Modal --- */}
      {recipeMenu && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh]">
          <div className="absolute inset-0 bg-black/30" onClick={() => setRecipeMenu(null)}></div>
          <div className="relative w-full max-w-lg bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out] max-h-[88vh] overflow-y-auto">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="font-semibold text-foreground-800">Recipe · {recipeMenu.name}</h3>
                <p className="text-xs text-foreground-500">Ingredients consumed per 1 sold</p>
              </div>
              <button onClick={() => setRecipeMenu(null)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-3">
              {invItems.length === 0 && (
                <p className="text-sm text-foreground-500">Add inventory items first, then build the recipe.</p>
              )}
              {recipeLines.map((line, idx) => {
                const inv = invById.get(line.inventoryItemId);
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <select
                      value={line.inventoryItemId}
                      onChange={(e) => setRecipeLines((prev) => prev.map((l, i) => i === idx ? { ...l, inventoryItemId: e.target.value } : l))}
                      className="flex-1 px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      <option value="">Select ingredient</option>
                      {invItems.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                    <input
                      type="number" placeholder="Qty"
                      value={line.qtyPerUnit}
                      onChange={(e) => setRecipeLines((prev) => prev.map((l, i) => i === idx ? { ...l, qtyPerUnit: e.target.value } : l))}
                      className="w-24 px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                    />
                    <span className="w-10 text-xs text-foreground-400">{inv?.unit ?? ''}</span>
                    <button
                      onClick={() => setRecipeLines((prev) => prev.filter((_, i) => i !== idx))}
                      className="w-8 h-8 rounded-md hover:bg-red-50 flex items-center justify-center transition-colors cursor-pointer shrink-0"
                    >
                      <i className="ri-close-line text-red-400"></i>
                    </button>
                  </div>
                );
              })}
              <button
                onClick={() => setRecipeLines((prev) => [...prev, { inventoryItemId: '', qtyPerUnit: '' }])}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              >
                <i className="ri-add-line mr-1"></i> Add ingredient
              </button>
              <div className="flex items-center justify-between pt-3 border-t border-background-200 text-sm">
                <span className="text-foreground-500">Est. food cost / plate</span>
                <span className="font-semibold text-foreground-800">₹{recipeCost.toFixed(2)}{recipeMenu.price > 0 && <span className="text-xs text-foreground-400 ml-1">({Math.round((recipeCost / recipeMenu.price) * 100)}% of price)</span>}</span>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button onClick={handleRecipeSave} disabled={saving} className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {saving ? 'Saving...' : 'Save Recipe'}
                </button>
                <button onClick={() => setRecipeMenu(null)} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- New Purchase Modal --- */}
      {purchaseModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh]">
          <div className="absolute inset-0 bg-black/30" onClick={() => setPurchaseModalOpen(false)}></div>
          <div className="relative w-full max-w-xl bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out] max-h-[88vh] overflow-y-auto">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-semibold text-foreground-800">New Purchase (Goods Received)</h3>
              <button onClick={() => setPurchaseModalOpen(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Supplier</label>
                  <input type="text" value={poSupplier} onChange={(e) => setPoSupplier(e.target.value)} placeholder="Supplier name" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Invoice / PO No.</label>
                  <input type="text" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
              </div>
              <div className="space-y-2">
                {poLines.map((line, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select
                      value={line.inventoryItemId}
                      onChange={(e) => setPoLines((prev) => prev.map((l, i) => i === idx ? { ...l, inventoryItemId: e.target.value, unitCost: l.unitCost || String(invById.get(e.target.value)?.unitCost ?? '') } : l))}
                      className="flex-1 px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      <option value="">Select item</option>
                      {invItems.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                    <input type="number" placeholder="Qty" value={line.qty}
                      onChange={(e) => setPoLines((prev) => prev.map((l, i) => i === idx ? { ...l, qty: e.target.value } : l))}
                      className="w-20 px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                    <input type="number" placeholder="₹/unit" value={line.unitCost}
                      onChange={(e) => setPoLines((prev) => prev.map((l, i) => i === idx ? { ...l, unitCost: e.target.value } : l))}
                      className="w-24 px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                    <button onClick={() => setPoLines((prev) => prev.filter((_, i) => i !== idx))}
                      className="w-8 h-8 rounded-md hover:bg-red-50 flex items-center justify-center transition-colors cursor-pointer shrink-0">
                      <i className="ri-close-line text-red-400"></i>
                    </button>
                  </div>
                ))}
                <button onClick={() => setPoLines((prev) => [...prev, { inventoryItemId: '', qty: '', unitCost: '' }])}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                  <i className="ri-add-line mr-1"></i> Add line
                </button>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-background-200 text-sm">
                <span className="text-foreground-500">Total</span>
                <span className="font-semibold text-foreground-800">₹{poTotal.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={handlePurchaseSave} disabled={saving} className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {saving ? 'Saving...' : 'Receive & Add Stock'}
                </button>
                <button onClick={() => setPurchaseModalOpen(false)} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Record Wastage Modal --- */}
      {wastageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]">
          <div className="absolute inset-0 bg-black/30" onClick={() => setWastageModalOpen(false)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out]">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-semibold text-foreground-800">Record Wastage</h3>
              <button onClick={() => setWastageModalOpen(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Item Name *</label>
                <input type="text" list="inventory-item-names" value={wastageForm.itemName} onChange={(e) => setWastageForm({ ...wastageForm, itemName: e.target.value })} placeholder="e.g. Fresh Paneer" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                <datalist id="inventory-item-names">
                  {invItems.map((i) => <option key={i.id} value={i.name} />)}
                </datalist>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Quantity *</label>
                  <input type="number" value={wastageForm.quantity} onChange={(e) => setWastageForm({ ...wastageForm, quantity: e.target.value })} placeholder="0" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Unit</label>
                  <select value={wastageForm.unit} onChange={(e) => setWastageForm({ ...wastageForm, unit: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                    {['kg','g','L','ml','pcs','pack'].map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Reason</label>
                <select value={wastageForm.reason} onChange={(e) => setWastageForm({ ...wastageForm, reason: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                  {['Spoilage','Overripe','Prep Trim','Overcooked','Spillage','Wrong Order','Other'].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3 pt-3 border-t border-background-200">
                <button onClick={handleWastageSave} disabled={saving} className="px-5 py-2 text-sm font-medium rounded-lg bg-red-500 text-background-50 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {saving ? 'Recording...' : 'Record Wastage'}
                </button>
                <button onClick={() => setWastageModalOpen(false)} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Delete Confirm Modal --- */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl shadow-lg mx-4 p-5 animate-[slideInUp_0.15s_ease-out]">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <h4 className="text-center font-semibold text-foreground-800 mb-1">Delete Item?</h4>
            <p className="text-center text-sm text-foreground-500 mb-4">"{deleteTarget.name}" will be permanently removed.</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => handleInvDelete(deleteTarget)} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-background-50 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}
